import { apiFetch } from "@/lib/api-client";

interface ApiEnvelope<T> {
  data: T;
}

export interface ReviewAnchorPosition {
  x: number;
  y: number;
}

export interface PageReviewCommentRecord {
  id: string;
  reviewId: string;
  versionId: string;
  componentId: string;
  anchorLabel: string | null;
  position: ReviewAnchorPosition;
  body: string;
  authorId: string;
  authorName: string;
  authorRole: "internal" | "client";
  resolved: boolean;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PageReviewDetailRecord {
  id: string;
  pageId: string;
  versionId: string;
  siteId: string | null;
  shareToken: string;
  status: "pending" | "changes_requested" | "approved";
  reviewerName: string | null;
  reviewerEmail: string | null;
  message: string | null;
  requestedBy: string;
  requestedAt: string;
  lastDecisionBy: string | null;
  lastDecisionAt: string | null;
  approvedAt: string | null;
  changesRequestedAt: string | null;
  createdAt: string;
  updatedAt: string;
  page: {
    id: string;
    title: string;
    slug: string;
    status: string;
    siteId: string | null;
    publishedUrl: string | null;
  };
  version: {
    id: string;
    versionNumber: number;
    puckData: { content?: Array<{ type: string; props?: Record<string, unknown> }> };
    createdAt: string;
    createdBy: string;
  };
  preview: {
    path: string;
    url: string;
    expiresAt: string | null;
  };
  comments: PageReviewCommentRecord[];
}

export interface PageReviewSummaryRecord {
  id: string;
  pageId: string;
  versionId: string;
  shareToken: string;
  status: "pending" | "changes_requested" | "approved";
  reviewerName: string | null;
  reviewerEmail: string | null;
  requestedAt: string;
}

export async function fetchPageReviews(pageId: string): Promise<PageReviewSummaryRecord[]> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord[]>>(`/crm/pages/${pageId}/reviews?limit=50`);
  return response.data ?? [];
}

export async function createPageReview(
  pageId: string,
  input: {
    versionId?: string;
    siteId?: string;
    reviewerName?: string;
    reviewerEmail?: string;
    message?: string;
  },
): Promise<PageReviewSummaryRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord>>(`/crm/pages/${pageId}/reviews`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function fetchPageReview(pageId: string, reviewId: string): Promise<PageReviewDetailRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewDetailRecord>>(`/crm/pages/${pageId}/reviews/${reviewId}`);
  return response.data;
}

export async function addPageReviewComment(
  pageId: string,
  reviewId: string,
  input: {
    versionId: string;
    componentId: string;
    anchorLabel?: string;
    body: string;
    authorName?: string;
    position?: ReviewAnchorPosition;
  },
): Promise<PageReviewCommentRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewCommentRecord>>(`/crm/pages/${pageId}/reviews/${reviewId}/comments`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function resolvePageReviewComment(pageId: string, reviewId: string, commentId: string): Promise<PageReviewCommentRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewCommentRecord>>(`/crm/pages/${pageId}/reviews/${reviewId}/comments/${commentId}/resolve`, {
    method: "POST",
  });
  return response.data;
}

export async function approvePageReview(pageId: string, reviewId: string): Promise<PageReviewSummaryRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord>>(`/crm/pages/${pageId}/reviews/${reviewId}/approve`, {
    method: "POST",
  });
  return response.data;
}

export async function requestChangesPageReview(pageId: string, reviewId: string): Promise<PageReviewSummaryRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord>>(`/crm/pages/${pageId}/reviews/${reviewId}/request-changes`, {
    method: "POST",
  });
  return response.data;
}

export async function fetchPublicPageReview(shareToken: string): Promise<PageReviewDetailRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewDetailRecord>>(`/api/public/reviews/${shareToken}`);
  return response.data;
}

export async function addPublicPageReviewComment(
  shareToken: string,
  input: {
    versionId: string;
    componentId: string;
    anchorLabel?: string;
    body: string;
    position?: ReviewAnchorPosition;
  },
): Promise<PageReviewCommentRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewCommentRecord>>(`/api/public/reviews/${shareToken}/comments`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function approvePublicPageReview(shareToken: string): Promise<PageReviewSummaryRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord>>(`/api/public/reviews/${shareToken}/approve`, {
    method: "POST",
  });
  return response.data;
}

export async function requestChangesPublicPageReview(shareToken: string): Promise<PageReviewSummaryRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewSummaryRecord>>(`/api/public/reviews/${shareToken}/request-changes`, {
    method: "POST",
  });
  return response.data;
}

export async function resolvePublicPageReviewComment(shareToken: string, commentId: string): Promise<PageReviewCommentRecord> {
  const response = await apiFetch<ApiEnvelope<PageReviewCommentRecord>>(`/api/public/reviews/${shareToken}/comments/${commentId}/resolve`, {
    method: "POST",
  });
  return response.data;
}
